import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { Sparkles, ChevronRight, ChevronLeft, Loader2, X } from "lucide-react";
import { Button } from "../components/ui/button";

const CHALLENGES = [
  "Trouver de nouveaux clients",
  "Augmenter le panier moyen",
  "Fidéliser les clients existants",
  "Améliorer la visibilité en ligne",
  "Automatiser des processus internes",
];

const TONES = [
  { value: "formel", label: "Formel", desc: "Institutionnel, soigné" },
  { value: "professionnel", label: "Professionnel", desc: "Clair, structuré" },
  { value: "direct", label: "Direct", desc: "Percutant, résultats" },
];

export default function ProposalCoach() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form state
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [challenges, setChallenges] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [tone, setTone] = useState("professionnel");

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const handleClientSelect = (id) => {
    setClientId(id);
    const c = clients.find((x) => x.id === id);
    setClientName(c ? (c.company || c.name) : "");
  };

  const addChallenge = (txt) => {
    setChallenges((prev) => prev ? `${prev}\n${txt}` : txt);
  };

  const generateAndGo = async () => {
    setLoading(true);
    try {
      const name = clientName.trim() || "Prospect";
      const title = `Proposition ${name}`;

      // 1. Créer la proposition vide
      const { data: prop } = await api.post("/proposals", { title, sections: [] });

      // 2. Générer avec l'IA
      const tpl = templates.find((t) => t.id === templateId);
      const offerType = tpl?.name || "Proposition commerciale";
      const aiRes = await api.post("/ai/generate", {
        title,
        client_name: name,
        brief: challenges.trim(),
        offer_type: offerType,
        tone,
      });

      // 3. Patcher la proposition avec les sections générées
      const sections = (aiRes.data.sections || []).map((s) => ({ ...s, items: s.items || [] }));
      await api.put(`/proposals/${prop.id}`, {
        title: aiRes.data.title || title,
        clientName: name,
        ...(clientId && { clientId }),
        sections,
      });

      navigate(`/proposals/${prop.id}/edit`);
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  const skipCoach = async () => {
    setLoading(true);
    try {
      const name = clientName.trim() || "Nouvelle proposition";
      const { data } = await api.post("/proposals", {
        title: `Proposition ${name}`,
        sections: [],
      });
      navigate(`/proposals/${data.id}/edit`);
    } catch {
      toast.error("Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const canNext1 = clientName.trim().length > 0;
  const canNext2 = challenges.trim().length > 0;

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col items-center justify-start pt-16 pb-16 px-4">
      {/* Header */}
      <div className="w-full max-w-xl mb-8 flex items-center justify-between">
        <button onClick={() => navigate("/proposals")} className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm">
          <X className="w-4 h-4" /> Annuler
        </button>
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">Nouvelle proposition</p>
        <button onClick={skipCoach} disabled={loading} className="text-xs text-muted-foreground hover:text-foreground">
          Passer
        </button>
      </div>

      {/* Progress */}
      <div className="w-full max-w-xl mb-10">
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className={`h-1 flex-1 rounded-full transition-colors ${n <= step ? 'bg-primary' : 'bg-border'}`} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Étape {step} sur 4</p>
      </div>

      {/* Step content */}
      <div className="w-full max-w-xl bg-background rounded-sm border border-border p-8 space-y-6">

        {/* Step 1 — Prospect */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold font-heading mb-1">Qui est le client ?</h2>
              <p className="text-sm text-muted-foreground">Choisissez un client existant ou saisissez un nom.</p>
            </div>
            {clients.length > 0 && (
              <div>
                <label className="text-sm font-medium block mb-2">Client existant</label>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {clients.map((c) => (
                    <button key={c.id} onClick={() => handleClientSelect(c.id)}
                      className={`w-full text-left px-3 py-2 rounded-sm text-sm border transition-colors ${clientId === c.id ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:border-foreground/30'}`}>
                      {c.company || c.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium block mb-1">Ou saisissez un nom</label>
              <input value={clientName} onChange={(e) => { setClientName(e.target.value); setClientId(""); }}
                placeholder="Ex : Entreprise Martin"
                className="w-full border border-border rounded-sm px-3 py-2 text-sm outline-none focus:border-primary bg-transparent" />
            </div>
          </div>
        )}

        {/* Step 2 — Enjeux */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold font-heading mb-1">Quel problème résolvez-vous ?</h2>
              <p className="text-sm text-muted-foreground">Pour {clientName || 'ce client'} — soyez précis, l'IA s'en servira.</p>
            </div>
            <textarea value={challenges} onChange={(e) => setChallenges(e.target.value)}
              placeholder="Ex : L'entreprise perd des clients face aux concurrents en ligne, leur site actuel ne génère aucun lead…"
              className="w-full border border-border rounded-sm px-3 py-2 text-sm outline-none focus:border-primary bg-transparent resize-none"
              rows={4} />
            <div>
              <p className="text-xs text-muted-foreground mb-2">Suggestions rapides :</p>
              <div className="flex flex-wrap gap-2">
                {CHALLENGES.map((c) => (
                  <button key={c} onClick={() => addChallenge(c)}
                    className="text-xs px-2.5 py-1 border border-border rounded-sm hover:border-foreground/40 transition-colors">
                    + {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — Format */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold font-heading mb-1">Quel format de proposition ?</h2>
              <p className="text-sm text-muted-foreground">Choisissez un modèle ou partez de zéro.</p>
            </div>
            <div className="space-y-2">
              <button onClick={() => setTemplateId("")}
                className={`w-full text-left px-3 py-2.5 rounded-sm text-sm border transition-colors ${!templateId ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:border-foreground/30'}`}>
                Proposition personnalisée (l'IA structure selon le brief)
              </button>
              {templates.map((t) => (
                <button key={t.id} onClick={() => setTemplateId(t.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-sm border transition-colors ${templateId === t.id ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/30'}`}>
                  <p className="text-sm font-medium">{t.name}</p>
                  {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4 — Ton */}
        {step === 4 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold font-heading mb-1">Quel registre ?</h2>
              <p className="text-sm text-muted-foreground">Le ton de votre proposition commerciale.</p>
            </div>
            <div className="space-y-2">
              {TONES.map((t) => (
                <button key={t.value} onClick={() => setTone(t.value)}
                  className={`w-full text-left px-4 py-3 rounded-sm border transition-colors ${tone === t.value ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/30'}`}>
                  <p className="text-sm font-semibold">{t.label}</p>
                  <p className="text-xs text-muted-foreground">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-2">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)} className="rounded-sm">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
          ) : <div />}

          {step < 4 ? (
            <Button onClick={() => setStep(s => s + 1)}
              disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
              className="rounded-sm">
              Suivant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={generateAndGo} disabled={loading} className="rounded-sm bg-primary">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Générer avec l'IA
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
